import React from 'react';
import { motion } from 'framer-motion';
import { ExternalLinkIcon } from 'lucide-react';
import { InteriorSlide } from './SlideShell';
import type { SlideProps } from './SlideShell';
interface Example {
  src: string;
  alt: string;
  brand: string;
  title: string;
  span: string;
  highlight?: boolean;
  href?: string;
}
const EXAMPLES: Example[] = [{
  src: 'https://cdn.magicpatterns.com/uploads/cSJACRaDYv7e9KmEokWdVH/Screenshot_2026-09-16_at_13.18.24.png',
  alt: 'Columvi teaser Snak asking what you would expect to see after three years of follow up in 2L DLBCL if a treatment truly has curative potential, with three answer options',
  brand: 'Columvi',
  title: 'Question-led teaser opening',
  span: 'lg:col-span-2 lg:row-span-2',
  highlight: true,
  href: 'https://player.snakker.io/review/b5-dIwW18VplXQJl2QJ5'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/rPnCmy8BSH7eP5v7uX9scL/Screenshot_2026-09-16_at_12.47.30.png',
  alt: 'Hemlibra aisle challenge Snak inviting HCPs to take part in anonymous live polls and compare themselves to peers',
  brand: 'Hemlibra',
  title: 'Aisle challenge live poll',
  span: 'lg:col-span-5'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/9rDNT8ZXuXs6GjyuMqT5bP/Screenshot_2026-09-16_at_12.47.20.png',
  alt: 'Vabysmo product deep-dive Snak menu with options for 3Ds, real-world data and safety',
  brand: 'Vabysmo',
  title: 'Product deep-dive menu',
  span: 'lg:col-span-5'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/kb1YmFiyZFXEkoCtxJEaGF/Screenshot_2026-09-16_at_12.47.12.png',
  alt: 'Roche focus areas Snak letting HCPs select a disease area such as lung, haematology, breast, liver or pipeline to explore',
  brand: 'Roche',
  title: 'Focus area selection',
  span: 'lg:col-span-7',
  href: 'https://player.snakker.io/review/9Zrnky3yiOy6anlTc_2D'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/fwxxMaVeRAXkKjUX1mTrmd/Screenshot_2026-09-10_at_15.33.44.png',
  alt: 'Seretide teaser Snak animation explaining that not all inhalers are handled correctly and that effective therapy depends on real-world treatment use',
  brand: 'Seretide',
  title: 'Animated insight',
  span: 'lg:col-span-3'
}];
export function SlideSnakExamples({}: SlideProps) {
  return <InteriorSlide surface data-id="element-513" data-mp="3sslwhlalrpua">
      <motion.header initial={{
      opacity: 0,
      y: 16
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5
    }} className="mb-[clamp(0.6rem,1.3vw,1.25rem)] flex flex-shrink-0 items-end justify-between gap-8" data-id="element-514" data-mp="3ssc4xt9wtamh">
        <div data-id="element-515" data-mp="kedawx6hzzbm">
          <p className="mb-2 text-[clamp(0.7rem,1vw,0.9rem)] font-bold uppercase tracking-[0.2em] text-[var(--brand)]" data-id="element-516" data-mp="ke3mhe13q081">
            Teaser examples
          </p>
          <h2 className="text-[clamp(1.8rem,3vw,2.9rem)] font-extrabold leading-tight tracking-[-0.04em] text-[var(--fg)]" data-id="element-517" data-mp="uc16tqfubcvd">
            What a teaser Snak looks like{' '}
            <span className="text-[var(--brand)]" data-id="element-518" data-mp="perike46t794">in practice</span>
          </h2>
        </div>
        <p className="hidden max-w-sm text-right text-sm leading-relaxed text-[var(--muted)] lg:block" data-id="element-519" data-mp="18zhzyxk4pdpe">
          Brand-compliant and fully referenced, on phone and on desktop.
        </p>
      </motion.header>

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-[clamp(0.5rem,1vw,1rem)] sm:grid-cols-2 lg:grid-cols-12 lg:grid-rows-2" data-id="element-520" data-mp="z2n46mf8pret">
        {EXAMPLES.map((example, index) => <motion.figure key={example.src} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.14 + index * 0.09,
        duration: 0.5
      }} className={`flex min-h-0 flex-col overflow-hidden rounded-2xl border bg-white p-[clamp(0.4rem,0.7vw,0.7rem)] shadow-sm ${example.span} ${example.highlight ? 'border-[var(--brand)]/40 ring-1 ring-[var(--brand)]/15' : 'border-[var(--fg)]/8'}`} data-id="element-521" data-mp="p969zhb6yaam" data-mpi={index}>
            {example.href ? <a href={example.href} target="_blank" rel="noopener noreferrer" aria-label={`Open the live ${example.title} Snak in a new tab`} className="group relative flex min-h-0 flex-1 items-center justify-center overflow-hidden rounded-xl bg-[var(--surface)] outline-none ring-[var(--brand)] transition focus-visible:ring-2" data-id="element-522" data-mp="1em5asm5t0zw5">
                <img src={example.src} alt={example.alt} className="h-full w-full object-contain" data-id="element-523" data-mp="uzfza3ttx6yy" />
                <span className="absolute inset-0 bg-[var(--brand)]/0 transition-colors group-hover:bg-[var(--brand)]/10" data-id="element-524" data-mp="lwety5csfjwc" />
                <span className="absolute bottom-2 right-2 flex items-center gap-1.5 rounded-full bg-[var(--brand)] px-2.5 py-1 text-[9px] font-bold uppercase tracking-[0.1em] text-white opacity-90 shadow-sm transition group-hover:opacity-100" data-id="element-525" data-mp="gzeuweecvheq">
                  <ExternalLinkIcon className="h-3 w-3" data-id="element-526" data-mp="2ivngrrvy3u6u" />
                  Open live Snak
                </span>
              </a> : <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden rounded-xl bg-[var(--surface)]" data-id="element-527" data-mp="2ntfjlgy4p0pt">
                <img src={example.src} alt={example.alt} className="h-full w-full object-contain" data-id="element-528" data-mp="2snt8d0iac9li" />
              </div>}

            <figcaption className="flex flex-shrink-0 items-baseline justify-between gap-2 pt-2" data-id="element-529" data-mp="1zz5u3ovyekhg">
              <div className="min-w-0" data-id="element-530" data-mp="24xpf9n2q3s8r">
                <p className="truncate text-[clamp(0.7rem,0.95vw,0.88rem)] font-extrabold leading-tight text-[var(--fg)]" data-id="element-531" data-mp="29upk5unu6oya">
                  {example.title}
                </p>
                <p className="mt-0.5 truncate text-[9px] font-bold uppercase tracking-[0.14em] text-[var(--muted)]" data-id="element-532" data-mp="3lae8apcttu0o">
                  {example.brand}
                </p>
              </div>
              <span className={`flex-shrink-0 rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.1em] ${example.highlight ? 'bg-[var(--brand)] text-white' : 'bg-[var(--brand)]/10 text-[var(--brand)]'}`} data-id="element-533" data-mp="1q6zjubf8tu5j">
                Teaser
              </span>
            </figcaption>
          </motion.figure>)}
      </div>
    </InteriorSlide>;
}